export type IndicatorPlacement = "overlay" | "panel";

export type IndicatorType =
  | "none"
  | "sma"
  | "ema"
  | "bollinger"
  | "donchian"
  | "ma_envelope"
  | "vwap"
  | "rainbow_ma"
  | "ichimoku"
  | "parabolic_sar"
  | "alligator"
  | "zigzag"
  | "fractal_bands"
  | "pivot_points"
  | "rsi"
  | "macd"
  | "stochastic"
  | "aroon"
  | "smi"
  | "williams_r"
  | "cci"
  | "roc"
  | "atr"
  | "adx"
  | "obv"
  | "awesome"
  | "dpo"
  | "volume";

export type IndicatorSeriesPoint = number | null;
export type IndicatorOverlaySeries = {
  primary: IndicatorSeriesPoint[];
  secondary?: IndicatorSeriesPoint[];
  tertiary?: IndicatorSeriesPoint[];
};

export type IndicatorConfig = {
  id: string;
  type: IndicatorType;
  period: number;
  color: string;
  lineWidth: number;
};

export const INDICATOR_META: Record<IndicatorType, { label: string; placement: IndicatorPlacement }> = {
  none: { label: "None", placement: "overlay" },
  sma: { label: "SMA", placement: "overlay" },
  ema: { label: "EMA", placement: "overlay" },
  bollinger: { label: "Bollinger Bands", placement: "overlay" },
  donchian: { label: "Donchian Channel", placement: "overlay" },
  ma_envelope: { label: "MA Envelope", placement: "overlay" },
  vwap: { label: "VWAP", placement: "overlay" },
  rainbow_ma: { label: "Rainbow MA", placement: "overlay" },
  ichimoku: { label: "Ichimoku", placement: "overlay" },
  parabolic_sar: { label: "Parabolic SAR", placement: "overlay" },
  alligator: { label: "Alligator", placement: "overlay" },
  zigzag: { label: "ZigZag", placement: "overlay" },
  fractal_bands: { label: "Fractal Bands", placement: "overlay" },
  pivot_points: { label: "Pivot Points", placement: "overlay" },
  rsi: { label: "RSI", placement: "panel" },
  macd: { label: "MACD", placement: "panel" },
  stochastic: { label: "Stochastic", placement: "panel" },
  aroon: { label: "Aroon", placement: "panel" },
  smi: { label: "SMI Ergodic", placement: "panel" },
  williams_r: { label: "Williams %R", placement: "panel" },
  cci: { label: "CCI", placement: "panel" },
  roc: { label: "ROC", placement: "panel" },
  atr: { label: "ATR", placement: "panel" },
  adx: { label: "ADX", placement: "panel" },
  obv: { label: "OBV", placement: "panel" },
  awesome: { label: "Awesome Oscillator", placement: "panel" },
  dpo: { label: "DPO", placement: "panel" },
  volume: { label: "Volume", placement: "panel" },
};

function clampPeriod(period: number): number {
  return Math.max(2, Math.floor(period || 14));
}

function resolveAdaptivePeriod(period: number, length: number): number {
  return Math.max(1, Math.min(clampPeriod(period), Math.max(1, Math.floor(length))));
}

function progressiveSma(data: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, data.length);
  return data.map((_, i) => {
    const from = Math.max(0, i - p + 1);
    let sum = 0;
    let count = 0;
    for (let j = from; j <= i; j++) {
      sum += data[j];
      count += 1;
    }
    return count > 0 ? sum / count : null;
  });
}

function progressiveEma(data: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, data.length);
  if (data.length === 0) return [];
  const alpha = 2 / (p + 1);
  const result: IndicatorSeriesPoint[] = [];
  let prev = data[0];
  result.push(prev);
  for (let i = 1; i < data.length; i++) {
    prev = alpha * data[i] + (1 - alpha) * prev;
    result.push(prev);
  }
  return result;
}

export function sma(data: number[], period: number): IndicatorSeriesPoint[] {
  return progressiveSma(data, period);
}

export function ema(data: number[], period: number): IndicatorSeriesPoint[] {
  return progressiveEma(data, period);
}

function rollingStdDev(data: number[], endIdx: number, period: number, mean: number): number {
  const startIdx = Math.max(0, endIdx - period + 1);
  let sumSquaredDiff = 0;
  let count = 0;
  for (let j = startIdx; j <= endIdx; j++) {
    const diff = data[j] - mean;
    sumSquaredDiff += diff * diff;
    count += 1;
  }
  return Math.sqrt(sumSquaredDiff / Math.max(1, count));
}

function bollinger(data: number[], period: number, stdDevMultiplier: number = 2): IndicatorOverlaySeries {
  const p = resolveAdaptivePeriod(period, data.length);
  const middle = sma(data, p);
  const upper: IndicatorSeriesPoint[] = [];
  const lower: IndicatorSeriesPoint[] = [];
  for (let i = 0; i < data.length; i++) {
    const avg = middle[i];
    if (avg === null) {
      upper.push(null);
      lower.push(null);
      continue;
    }
    const stdDev = rollingStdDev(data, i, p, avg);
    upper.push(avg + stdDevMultiplier * stdDev);
    lower.push(avg - stdDevMultiplier * stdDev);
  }
  return { primary: middle, secondary: upper, tertiary: lower };
}

function donchian(data: number[], period: number): IndicatorOverlaySeries {
  const p = resolveAdaptivePeriod(period, data.length);
  const upper: IndicatorSeriesPoint[] = [];
  const lower: IndicatorSeriesPoint[] = [];
  const middle: IndicatorSeriesPoint[] = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - p + 1);
    let hi = -Infinity;
    let lo = Infinity;
    for (let j = start; j <= i; j++) {
      if (data[j] > hi) hi = data[j];
      if (data[j] < lo) lo = data[j];
    }
    upper.push(hi);
    lower.push(lo);
    middle.push((hi + lo) / 2);
  }
  return { primary: middle, secondary: upper, tertiary: lower };
}

function maEnvelope(data: number[], period: number, offsetPercent: number = 2): IndicatorOverlaySeries {
  const middle = sma(data, period);
  const upper: IndicatorSeriesPoint[] = [];
  const lower: IndicatorSeriesPoint[] = [];
  const ratio = Math.max(0, offsetPercent) / 100;
  for (const value of middle) {
    if (value === null) {
      upper.push(null);
      lower.push(null);
    } else {
      upper.push(value * (1 + ratio));
      lower.push(value * (1 - ratio));
    }
  }
  return { primary: middle, secondary: upper, tertiary: lower };
}

function vwap(closes: number[]): IndicatorSeriesPoint[] {
  const out: IndicatorSeriesPoint[] = [];
  let sumPriceVolume = 0;
  let sumVolume = 0;
  for (let i = 0; i < closes.length; i++) {
    const volume = 1;
    sumPriceVolume += closes[i] * volume;
    sumVolume += volume;
    out.push(sumVolume > 0 ? sumPriceVolume / sumVolume : closes[i]);
  }
  return out;
}

function rsi(closes: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, closes.length);
  const out: IndicatorSeriesPoint[] = closes.map(() => null);
  if (closes.length === 0) return out;
  out[0] = 50;
  for (let i = 1; i < closes.length; i++) {
    const start = Math.max(1, i - p + 1);
    let gain = 0;
    let loss = 0;
    let count = 0;
    for (let j = start; j <= i; j++) {
      const change = closes[j] - closes[j - 1];
      if (change >= 0) gain += change;
      else loss += -change;
      count += 1;
    }
    const avgGain = gain / Math.max(1, count);
    const avgLoss = loss / Math.max(1, count);
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

function macd(closes: number[]): IndicatorOverlaySeries {
  if (closes.length === 0) {
    return { primary: [] };
  }
  const slowPeriod = Math.max(2, Math.min(26, closes.length));
  const fastPeriod = Math.max(2, Math.min(12, Math.max(2, Math.floor(slowPeriod / 2))));
  const signalPeriod = Math.max(2, Math.min(9, Math.max(2, Math.floor(closes.length / 3))));
  const fast = ema(closes, fastPeriod);
  const slow = ema(closes, slowPeriod);
  const macdLine: IndicatorSeriesPoint[] = closes.map((_, i) =>
    fast[i] == null || slow[i] == null ? null : (fast[i] as number) - (slow[i] as number),
  );
  const macdNoNull = macdLine.map((v) => v ?? 0);
  const signal = ema(macdNoNull, signalPeriod).map((v) => v);
  const histogram: IndicatorSeriesPoint[] = closes.map((_, i) =>
    macdLine[i] == null || signal[i] == null ? null : (macdLine[i] as number) - (signal[i] as number),
  );
  return { primary: macdLine, secondary: signal, tertiary: histogram };
}

function stochastic(closes: number[], period: number): IndicatorOverlaySeries {
  const p = resolveAdaptivePeriod(period, closes.length);
  const k: IndicatorSeriesPoint[] = [];
  for (let i = 0; i < closes.length; i++) {
    const start = Math.max(0, i - p + 1);
    let lo = Infinity;
    let hi = -Infinity;
    for (let j = start; j <= i; j++) {
      if (closes[j] < lo) lo = closes[j];
      if (closes[j] > hi) hi = closes[j];
    }
    const range = hi - lo;
    k.push(range === 0 ? 50 : ((closes[i] - lo) / range) * 100);
  }
  const d = sma(k.map((v) => v ?? 0), Math.min(3, Math.max(1, closes.length))).map((v) => v);
  return { primary: k, secondary: d };
}

function williamsR(closes: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, closes.length);
  const out: IndicatorSeriesPoint[] = [];
  for (let i = 0; i < closes.length; i++) {
    const start = Math.max(0, i - p + 1);
    let lo = Infinity;
    let hi = -Infinity;
    for (let j = start; j <= i; j++) {
      if (closes[j] < lo) lo = closes[j];
      if (closes[j] > hi) hi = closes[j];
    }
    const range = hi - lo;
    out.push(range === 0 ? -50 : ((hi - closes[i]) / range) * -100);
  }
  return out;
}

function roc(closes: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, closes.length);
  return closes.map((price, i) => {
    const anchorIndex = Math.max(0, i - p);
    const anchor = closes[anchorIndex];
    if (anchor === 0) return 0;
    return ((price - anchor) / anchor) * 100;
  });
}

function cci(closes: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, closes.length);
  const ma = sma(closes, p);
  const out: IndicatorSeriesPoint[] = [];
  for (let i = 0; i < closes.length; i++) {
    const mean = ma[i];
    if (mean == null) {
      out.push(null);
      continue;
    }
    const start = Math.max(0, i - p + 1);
    let dev = 0;
    let count = 0;
    for (let j = start; j <= i; j++) {
      dev += Math.abs(closes[j] - mean);
      count += 1;
    }
    const meanDev = dev / Math.max(1, count);
    out.push(meanDev === 0 ? 0 : (closes[i] - mean) / (0.015 * meanDev));
  }
  return out;
}

function atr(closes: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, closes.length);
  const tr: number[] = closes.map((c, i) => (i === 0 ? 0 : Math.abs(c - closes[i - 1])));
  return ema(tr, p);
}

function adx(closes: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, closes.length);
  const absRoc = closes.map((_, i) => (i === 0 ? 0 : Math.abs(((closes[i] - closes[i - 1]) / closes[i - 1]) * 100)));
  return ema(absRoc, p);
}

function obv(closes: number[]): IndicatorSeriesPoint[] {
  const out: IndicatorSeriesPoint[] = [];
  let value = 0;
  for (let i = 0; i < closes.length; i++) {
    if (i === 0) {
      out.push(0);
      continue;
    }
    if (closes[i] > closes[i - 1]) value += 1;
    else if (closes[i] < closes[i - 1]) value -= 1;
    out.push(value);
  }
  return out;
}

function awesome(closes: number[]): IndicatorSeriesPoint[] {
  const shortPeriod = Math.max(2, Math.min(5, Math.max(2, Math.floor(closes.length / 2))));
  const longPeriod = Math.max(shortPeriod + 1, Math.min(34, Math.max(shortPeriod + 1, closes.length)));
  const sma5 = sma(closes, shortPeriod);
  const sma34 = sma(closes, longPeriod);
  return closes.map((_, i) =>
    sma5[i] == null || sma34[i] == null ? null : (sma5[i] as number) - (sma34[i] as number),
  );
}

function dpo(closes: number[], period: number): IndicatorSeriesPoint[] {
  const p = resolveAdaptivePeriod(period, closes.length);
  const ma = sma(closes, p);
  const shift = Math.floor(p / 2) + 1;
  return closes.map((price, i) => price - (ma[Math.max(0, i - shift)] ?? price));
}

function volume(closes: number[]): IndicatorSeriesPoint[] {
  return closes.map((_, i) => (i === 0 ? 0 : Math.abs(closes[i] - closes[i - 1])));
}

function calculateAroonSeries(data: number[], period: number): IndicatorOverlaySeries {
  const p = Math.max(2, Math.floor(period || 25));
  const up: IndicatorSeriesPoint[] = [];
  const down: IndicatorSeriesPoint[] = [];

  for (let i = 0; i < data.length; i++) {
    if (i < p - 1) {
      up.push(null);
      down.push(null);
      continue;
    }

    let highest = -Infinity;
    let lowest = Infinity;
    let highestIndex = i;
    let lowestIndex = i;
    for (let j = i - p + 1; j <= i; j++) {
      if (data[j] >= highest) {
        highest = data[j];
        highestIndex = j;
      }
      if (data[j] <= lowest) {
        lowest = data[j];
        lowestIndex = j;
      }
    }

    up.push(((p - (i - highestIndex)) / p) * 100);
    down.push(((p - (i - lowestIndex)) / p) * 100);
  }

  return { primary: up, secondary: down };
}

function calculateSMISeries(data: number[], kPeriod: number = 13, dPeriod: number = 25, signalPeriod: number = 9): IndicatorOverlaySeries {
  const period = Math.max(2, Math.floor(kPeriod || 13));
  const smooth = Math.max(2, Math.floor(dPeriod || 25));
  const signal = Math.max(2, Math.floor(signalPeriod || 9));

  const raw: (number | null)[] = [];
  const rangeSeries: (number | null)[] = [];

  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      raw.push(null);
      rangeSeries.push(null);
      continue;
    }

    let highest = -Infinity;
    let lowest = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      if (data[j] > highest) highest = data[j];
      if (data[j] < lowest) lowest = data[j];
    }
    const midpoint = (highest + lowest) / 2;
    raw.push(data[i] - midpoint);
    rangeSeries.push(highest - lowest);
  }

  const emaFromValues = (values: (number | null)[], length: number): (number | null)[] => {
    const out: (number | null)[] = [];
    const alpha = 2 / (length + 1);
    let prev: number | null = null;
    for (const value of values) {
      if (value === null) {
        out.push(null);
        continue;
      }
      prev = prev === null ? value : (value - prev) * alpha + prev;
      out.push(prev);
    }
    return out;
  };

  const smoothedRaw = emaFromValues(emaFromValues(raw, smooth), smooth);
  const smoothedRange = emaFromValues(emaFromValues(rangeSeries, smooth), smooth);
  const smiLine: IndicatorSeriesPoint[] = data.map((_, i) => {
    const num = smoothedRaw[i];
    const den = smoothedRange[i];
    if (num === null || den === null || den === 0) return null;
    return 100 * (num / (0.5 * den));
  });
  const signalLine = emaFromValues(smiLine, signal);
  return { primary: smiLine, secondary: signalLine };
}

export type IchimokuSeries = {
  tenkan: IndicatorSeriesPoint[];
  kijun: IndicatorSeriesPoint[];
  senkouA: IndicatorSeriesPoint[];
  senkouB: IndicatorSeriesPoint[];
  chikou: IndicatorSeriesPoint[];
};

function highestHigh(data: number[], endIndex: number, period: number): number | null {
  if (endIndex < period - 1) return null;
  let highest = data[endIndex];
  for (let i = endIndex - period + 1; i <= endIndex; i++) {
    if (data[i] > highest) highest = data[i];
  }
  return highest;
}

function lowestLow(data: number[], endIndex: number, period: number): number | null {
  if (endIndex < period - 1) return null;
  let lowest = data[endIndex];
  for (let i = endIndex - period + 1; i <= endIndex; i++) {
    if (data[i] < lowest) lowest = data[i];
  }
  return lowest;
}

export function calculateRainbowMA(data: number[], periods: number[] = [8, 13, 21, 34, 55, 89, 144, 233]): IndicatorSeriesPoint[][] {
  return periods.map((period) => ema(data, period));
}

export function calculateIchimoku(data: number[], tenkanPeriod = 9, kijunPeriod = 26, senkouPeriod = 52): IchimokuSeries {
  const tenkan: IndicatorSeriesPoint[] = [];
  const kijun: IndicatorSeriesPoint[] = [];
  const senkouA: IndicatorSeriesPoint[] = [];
  const senkouB: IndicatorSeriesPoint[] = [];
  const chikou: IndicatorSeriesPoint[] = [];
  const displacement = Math.max(1, Math.floor(kijunPeriod));

  for (let i = 0; i < data.length; i++) {
    const tenkanHigh = highestHigh(data, i, tenkanPeriod);
    const tenkanLow = lowestLow(data, i, tenkanPeriod);
    const kijunHigh = highestHigh(data, i, kijunPeriod);
    const kijunLow = lowestLow(data, i, kijunPeriod);
    const tenkanValue = tenkanHigh !== null && tenkanLow !== null ? (tenkanHigh + tenkanLow) / 2 : null;
    const kijunValue = kijunHigh !== null && kijunLow !== null ? (kijunHigh + kijunLow) / 2 : null;
    tenkan.push(tenkanValue);
    kijun.push(kijunValue);
    chikou.push(data[i]);

    if (i >= displacement) {
      const pastTenkan = tenkan[i - displacement];
      const pastKijun = kijun[i - displacement];
      senkouA.push(pastTenkan !== null && pastKijun !== null ? (pastTenkan + pastKijun) / 2 : null);
      const pastIndex = i - displacement;
      const senkouBHigh = highestHigh(data, pastIndex, senkouPeriod);
      const senkouBLow = lowestLow(data, pastIndex, senkouPeriod);
      senkouB.push(senkouBHigh !== null && senkouBLow !== null ? (senkouBHigh + senkouBLow) / 2 : null);
    } else {
      senkouA.push(null);
      senkouB.push(null);
    }
  }

  return { tenkan, kijun, senkouA, senkouB, chikou };
}

export type ParabolicSARSeries = (number | null)[];

export function calculateParabolicSAR(data: number[], acceleration = 0.02, maximum = 0.2): ParabolicSARSeries {
  if (data.length < 2) return data.map(() => null);

  const highs = data;
  const lows = data;
  const result: ParabolicSARSeries = [];
  result.push(null);

  let uptrend = data[1] > data[0];
  let sar = uptrend ? lows[0] : highs[0];
  let ep = uptrend ? highs[1] : lows[1];
  let af = acceleration;
  result.push(sar);

  for (let i = 2; i < data.length; i++) {
    let nextSar = sar + af * (ep - sar);
    if (uptrend) {
      nextSar = Math.min(nextSar, lows[i - 1], lows[i - 2]);
      if (data[i] < nextSar) {
        uptrend = false;
        nextSar = ep;
        ep = data[i];
        af = acceleration;
      } else if (data[i] > ep) {
        ep = data[i];
        af = Math.min(maximum, af + acceleration);
      }
    } else {
      nextSar = Math.max(nextSar, highs[i - 1], highs[i - 2]);
      if (data[i] > nextSar) {
        uptrend = true;
        nextSar = ep;
        ep = data[i];
        af = acceleration;
      } else if (data[i] < ep) {
        ep = data[i];
        af = Math.min(maximum, af + acceleration);
      }
    }
    sar = nextSar;
    result.push(sar);
  }

  return result;
}

export function calculateAlligator(data: number[], jawPeriod = 13, teethPeriod = 8, lipsPeriod = 5, jawShift = 8, teethShift = 5, lipsShift = 3): IndicatorOverlaySeries {
  const shiftSeries = (series: IndicatorSeriesPoint[], shift: number): IndicatorSeriesPoint[] => {
    const out: IndicatorSeriesPoint[] = Array.from({ length: series.length }, () => null);
    for (let i = 0; i < series.length; i++) {
      const target = i + shift;
      if (target < out.length) out[target] = series[i];
    }
    return out;
  };

  return {
    primary: shiftSeries(sma(data, jawPeriod), jawShift),
    secondary: shiftSeries(sma(data, teethPeriod), teethShift),
    tertiary: shiftSeries(sma(data, lipsPeriod), lipsShift),
  };
}

export type ZigZagPoint = { index: number; price: number };

export function calculateZigZag(data: number[], deviation = 5, depth = 12): IndicatorSeriesPoint[] {
  if (data.length < depth * 2) return data.map(() => null);

  const pivots: ZigZagPoint[] = [];
  const deviationDecimal = deviation / 100;
  let lastPivot: ZigZagPoint | null = null;
  let trend: "up" | "down" = "up";
  let extremeHigh = data[0];
  let extremeLow = data[0];
  let extremeHighIndex = 0;
  let extremeLowIndex = 0;

  for (let i = 1; i < data.length; i++) {
    const price = data[i];
    if (price > extremeHigh) {
      extremeHigh = price;
      extremeHighIndex = i;
    }
    if (price < extremeLow) {
      extremeLow = price;
      extremeLowIndex = i;
    }

    if (!lastPivot) {
      const upMove = (extremeHigh - data[0]) / Math.max(0.000001, data[0]);
      const downMove = (data[0] - extremeLow) / Math.max(0.000001, data[0]);
      if (upMove >= deviationDecimal && i - extremeHighIndex >= depth) {
        lastPivot = { index: extremeHighIndex, price: extremeHigh };
        pivots.push(lastPivot);
        trend = "down";
        extremeLow = price;
        extremeLowIndex = i;
      } else if (downMove >= deviationDecimal && i - extremeLowIndex >= depth) {
        lastPivot = { index: extremeLowIndex, price: extremeLow };
        pivots.push(lastPivot);
        trend = "up";
        extremeHigh = price;
        extremeHighIndex = i;
      }
      continue;
    }

    if (trend === "down") {
      const moveDown = (lastPivot.price - extremeLow) / Math.max(0.000001, lastPivot.price);
      if (moveDown >= deviationDecimal && i - extremeLowIndex >= depth) {
        lastPivot = { index: extremeLowIndex, price: extremeLow };
        pivots.push(lastPivot);
        trend = "up";
        extremeHigh = price;
        extremeHighIndex = i;
      }
    } else {
      const moveUp = (extremeHigh - lastPivot.price) / Math.max(0.000001, lastPivot.price);
      if (moveUp >= deviationDecimal && i - extremeHighIndex >= depth) {
        lastPivot = { index: extremeHighIndex, price: extremeHigh };
        pivots.push(lastPivot);
        trend = "down";
        extremeLow = price;
        extremeLowIndex = i;
      }
    }
  }

  const out: IndicatorSeriesPoint[] = data.map(() => null);
  for (const pivot of pivots) out[pivot.index] = pivot.price;
  return out;
}

export type FractalBandsSeries = {
  upperBand: IndicatorSeriesPoint[];
  lowerBand: IndicatorSeriesPoint[];
};

export function calculateFractalBands(data: number[], leftBars = 2, rightBars = 2): FractalBandsSeries {
  const upperBand: IndicatorSeriesPoint[] = [];
  const lowerBand: IndicatorSeriesPoint[] = [];
  let lastHigh: number | null = null;
  let lastLow: number | null = null;

  const isFractalHigh = (index: number): boolean => {
    if (index < leftBars || index >= data.length - rightBars) return false;
    const center = data[index];
    for (let i = 1; i <= leftBars; i++) if (data[index - i] >= center) return false;
    for (let i = 1; i <= rightBars; i++) if (data[index + i] >= center) return false;
    return true;
  };

  const isFractalLow = (index: number): boolean => {
    if (index < leftBars || index >= data.length - rightBars) return false;
    const center = data[index];
    for (let i = 1; i <= leftBars; i++) if (data[index - i] <= center) return false;
    for (let i = 1; i <= rightBars; i++) if (data[index + i] <= center) return false;
    return true;
  };

  for (let i = 0; i < data.length; i++) {
    if (isFractalHigh(i)) lastHigh = data[i];
    if (isFractalLow(i)) lastLow = data[i];
    upperBand.push(lastHigh);
    lowerBand.push(lastLow);
  }

  return { upperBand, lowerBand };
}

export type PivotLevels = {
  pp: number;
  r1: number;
  r2: number;
  r3: number;
  s1: number;
  s2: number;
  s3: number;
};

export type PivotPointsSeries = {
  levels: (PivotLevels | null)[];
};

export function calculatePivotPoints(data: number[], period = 20): PivotPointsSeries {
  if (data.length === 0) return { levels: [] };

  const levels: (PivotLevels | null)[] = data.map(() => null);
  for (let i = 0; i < data.length; i++) {
    if (i < period) continue;
    let high = -Infinity;
    let low = Infinity;
    for (let j = i - period; j < i; j++) {
      if (data[j] > high) high = data[j];
      if (data[j] < low) low = data[j];
    }
    const close = data[i - 1];
    const pp = (high + low + close) / 3;
    levels[i] = {
      pp,
      r1: 2 * pp - low,
      r2: pp + (high - low),
      r3: high + 2 * (pp - low),
      s1: 2 * pp - high,
      s2: pp - (high - low),
      s3: low - 2 * (high - pp),
    };
  }

  return { levels };
}

export function getIndicatorPlacement(type: IndicatorType): IndicatorPlacement {
  return INDICATOR_META[type].placement;
}

export function computeIndicatorSeries(data: number[], type: IndicatorType, period: number): IndicatorOverlaySeries {
  switch (type) {
    case "sma":
      return { primary: sma(data, period) };
    case "ema":
      return { primary: ema(data, period) };
    case "bollinger":
      return bollinger(data, period, 2);
    case "donchian":
      return donchian(data, period);
    case "ma_envelope":
      return maEnvelope(data, period, 2);
    case "vwap":
      return { primary: vwap(data) };
    case "aroon":
      return calculateAroonSeries(data, period);
    case "smi":
      return calculateSMISeries(data);
    case "ichimoku": {
      const series = calculateIchimoku(data);
      return { primary: series.tenkan, secondary: series.kijun, tertiary: series.senkouA };
    }
    case "parabolic_sar":
      return { primary: calculateParabolicSAR(data) };
    case "alligator": {
      const series = calculateAlligator(data);
      return { primary: series.primary, secondary: series.secondary, tertiary: series.tertiary };
    }
    case "zigzag":
      return { primary: calculateZigZag(data) };
    case "fractal_bands": {
      const series = calculateFractalBands(data);
      return { primary: series.upperBand, secondary: series.lowerBand };
    }
    case "pivot_points": {
      const series = calculatePivotPoints(data, period);
      return { primary: series.levels.map((v) => v?.pp ?? null), secondary: series.levels.map((v) => v?.r1 ?? null), tertiary: series.levels.map((v) => v?.s1 ?? null) };
    }
    case "rsi":
      return { primary: rsi(data, period) };
    case "macd":
      return macd(data);
    case "stochastic":
      return stochastic(data, period);
    case "williams_r":
      return { primary: williamsR(data, period) };
    case "cci":
      return { primary: cci(data, period) };
    case "roc":
      return { primary: roc(data, period) };
    case "atr":
      return { primary: atr(data, period) };
    case "adx":
      return { primary: adx(data, period) };
    case "obv":
      return { primary: obv(data) };
    case "awesome":
      return { primary: awesome(data) };
    case "dpo":
      return { primary: dpo(data, period) };
    case "volume":
      return { primary: volume(data) };
    default:
      return { primary: data.map(() => null) };
  }
}
